import qs from 'qs';
import mergeWith from 'lodash/mergeWith';
import { useHistory, useRouteMatch } from 'react-router-dom';
import {
  parseQueryString,
  createQueryString,
  removeEmptyItems,
  convertEmptyValuesToNull,
} from 'common/utils/queryStringUtils';
import { dayOnlyDateFormat, defaultRange } from 'common/utils/date';
import { defaultComparisonOption } from 'common/constants/comparisonOptions';
import { trendsFilterDuplicator } from 'common/components/Nav/TrendsFilterDuplicator';
import { useMemo } from 'react';

export const defaultFilters = {
  startDate: defaultRange.range.startDate.toFormat(dayOnlyDateFormat),
  startDateTime: null,
  endDate: defaultRange.range.endDate.toFormat(dayOnlyDateFormat),
  endDateTime: null,
  solicitationReportingTimestamp: null,
  employeeId: null,
  employeeIds: [],
  ownerIds: [],
  participantIds: [],
  teamId: null,
  teamIds: [],
  groupIds: [],
  reportingGroupIds: [],
  scorecardId: null,
  reviewerIds: [],
  qaFormIds: [],
  languages: [],
  brands: [],
  channelEnumIds: [],
  propertyIds: [],
  tags: [],
  excludeTags: false,
  includeAudited: true,
  autofailed: false,
  drafts: false,
  archived: false,
  compareTo: defaultComparisonOption.value,
  reviewEvents: [],
  assignmentIds: [],
  auditorIds: [],
  origins: [],

  // please don't use these. Leaving them for now for backwards compat.
  dateFrom: defaultRange.range.startDate.toFormat(dayOnlyDateFormat),
  dateTo: defaultRange.range.endDate.toFormat(dayOnlyDateFormat),
};

function toInt(x) {
  const int = parseInt(x, 10);
  if (int.toString() !== x) {
    return null;
  }
  return int;
}

function toArray(value) {
  if (Array.isArray(value)) return value;
  if (value === undefined || value === null || value === '') return [];
  return [value];
}

export function parsedQueryStringFilter(locationSearch) {
  return parseQueryString(locationSearch);
}

export function getSearchFilter(history, overrideDefaults = {}) {
  const {
    location: { pathname, search },
  } = history;
  const defaultFilterValues = { ...defaultFilters, ...overrideDefaults };

  const filter = parsedQueryStringFilter(search);

  filter.employeeId = filter.employeeId || filter.employee_id;
  delete filter.employee_id;

  filter.employeeId = filter.employeeId === undefined ? null : toInt(filter.employeeId);
  filter.teamId = filter.teamId === undefined ? null : toInt(filter.teamId);
  filter.scorecardId = filter.scorecardId === undefined ? null : toInt(filter.scorecardId);

  Object.entries(defaultFilterValues).forEach(([filterKey, defaultValue]) => {
    if (filter[filterKey] === undefined) {
      filter[filterKey] = defaultValue;
    }
  });

  if (!document.getElementById('assignmentsFilterSelect')) {
    filter.assignmentIds = [];
  }

  if (!document.getElementById('originsFilterSelect')) {
    filter.origins = [];
  }

  filter.employeeIds = toArray(filter.employeeIds).map(toInt);
  filter.ownerIds = toArray(filter.ownerIds).map(toInt);
  filter.participantIds = toArray(filter.participantIds).map(toInt);
  filter.teamIds = toArray(filter.teamIds).map(toInt);
  filter.groupIds = toArray(filter.groupIds).map(toInt);
  filter.reportingGroupIds = toArray(filter.reportingGroupIds).map(toInt);
  filter.reviewerIds = toArray(filter.reviewerIds).map(toInt);
  filter.channelEnumIds = toArray(filter.channelEnumIds).map(toInt);
  filter.propertyIds = toArray(filter.propertyIds).map(toInt);
  filter.assignmentIds = toArray(filter.assignmentIds).map(toInt);
  filter.auditorIds = toArray(filter.auditorIds).map(toInt);
  filter.autofailed = filter.autofailed === 'true';
  filter.excludeTags = filter.excludeTags === 'true';
  filter.includeAudited = filter.includeAudited === true || filter.includeAudited === 'true';
  filter.drafts = filter.drafts === 'true';
  filter.archived = filter.archived === 'true';
  filter.reviewed = filter.reviewed === 'true';
  filter.showSummary = filter.showSummary === 'true';
  filter.includeDeactivatedForTeams = filter.includeDeactivatedForTeams === 'true';
  filter.includeDeactivatedForGroups = filter.includeDeactivatedForGroups === 'true';

  if (pathname === '/qa/reports/scorecard') {
    filter.qaFormIds = [filter.scorecardId];
  } else if (['/qa/reports/overview', '/qa/reports/teammembers'].includes(pathname)) {
    // these reports don't accept this parameter
    filter.qaFormIds = [];
  } else if (['/qa/interactions'].includes(pathname)) {
    filter.qaFormIds = [];
    filter.propertyIds = [];
    filter.autofailed = null;
  } else {
    filter.qaFormIds = filter.qaFormIds.map(toInt);
  }

  return filter;
}

export function setSearchFilter(history, fieldsToValues, pushToNavigationStack) {
  const { location } = history;
  const modifyHistory = pushToNavigationStack ? history.push : history.replace;

  const oldSearch = parseQueryString(location.search);

  const newQueryParams = convertEmptyValuesToNull(parseQueryString(fieldsToValues));
  const search = removeEmptyItems(
    mergeWith(oldSearch, newQueryParams, (current, newValue) => {
      if (Array.isArray(current)) {
        return newValue;
      }
      // Return `undefined` in order to use the default behavior of the merge function from Lodash
      // (i.e. replace the values of the source object with the ones on the target object).
      return undefined;
    })
  );

  modifyHistory({ search: createQueryString(search) });
}

export function clearSearchFilter(history) {
  history.replace({
    search: createQueryString({}),
  });
}

export const useClearSearchFilter = () => {
  const history = useHistory();
  return useMemo(() => () => clearSearchFilter(history), [history]);
};

export function concatTrendsFlexFilters() {
  const trendsClassName = 'a.js-trends-filter-link';
  const navbarDomElements = document.querySelectorAll(trendsClassName);
  navbarDomElements.forEach(element => {
    element.addEventListener('click', () => {
      const newUrl = trendsFilterDuplicator.modifyUrl(window.location, element.href);
      element.setAttribute('href', newUrl);
    });
  });
}

/*
Using url params here to be backwards-compatible with old urls.
I suggest to use query params + the above "getSearchFilter" func when possible.
  --Ethan, 2020-11-18
*/
export function useSessionIdUrlParam() {
  const rawSessionId = useRouteMatch().params.sessionId;
  return toInt(rawSessionId);
}

export const objectToQueryParams = params => {
  return qs.stringify(params);
};
